import { View, StyleSheet, ScrollView } from 'react-native';
import { useNavigate } from 'react-router';
import { Sidebar, Text, Icon, theme } from '@socrait/react';

import { TabBar, type TabConfig } from '~/components/TabBar';
import { HyperLink } from '~/components/HyperLink';
import { useLogoutMutation } from '~/services/useLogoutMutation';
import { useUserProfile } from '~/services/useUserProfileQuery';

type AdminTab = 'overview' | 'students';

const PADDING = 40;
const HEADER_HEIGHT = 96;

const TABS: readonly TabConfig<AdminTab>[] = [
  { key: 'overview', label: 'Overview', telemetryLabel: 'admin-tab-overview' },
  { key: 'students', label: 'Students', telemetryLabel: 'admin-tab-students' },
];

type AdminLayoutProps = {
  children: React.ReactNode;
  /** Highlights a top-level tab. Ignored on sub-pages (those with `parentCrumb`), which hide the tabs. */
  activeTab?: AdminTab;
  scrollable?: boolean;
  /**
   * Breadcrumb-style segments joined with " - " to form the header title.
   * When showSchoolName is true (default), the school name is prepended automatically.
   */
  titleSegments?: string[];
  /** Set to false on sub-pages where the school name would be redundant. Defaults to true. */
  showSchoolName?: boolean;
  /**
   * A clickable ancestor crumb rendered before the title (e.g. { label: 'Students', to: '/students' }).
   * Marks the page as a sub-page: the top-level tabs are hidden and this crumb becomes the "back"
   * affordance, so the breadcrumb (not a falsely-highlighted tab) carries navigation.
   */
  parentCrumb?: { label: string; to: string };
  /**
   * Context (e.g. a student's class + teacher) shown on the far right of a sub-page, in place of the
   * tab bar. Only rendered when `parentCrumb` is set.
   */
  headerMeta?: string;
};

export function AdminLayout({
  children,
  activeTab,
  scrollable = true,
  titleSegments,
  showSchoolName = true,
  parentCrumb,
  headerMeta,
}: AdminLayoutProps) {
  const { user } = useUserProfile();
  const { mutate: logout } = useLogoutMutation();
  const navigate = useNavigate();

  const schoolName = user?.schoolName ?? 'School Dashboard';
  const titleParts = titleSegments?.length
    ? showSchoolName
      ? [schoolName, ...titleSegments]
      : titleSegments
    : [schoolName];
  const title = titleParts.join(' - ');

  return (
    <View style={styles.container}>
      <Sidebar
        isSimple={true}
        containerStyle={styles.sidebar}
        sidebarButtons={[
          {
            expandedText: 'Log out',
            iconType: 'logout',
            onPress: () => logout(),
            telemetryLabel: 'admin-sidebar-logout',
          },
        ]}
      />

      <View style={styles.main}>
        <View style={styles.header}>
          <View style={[styles.headerRow, parentCrumb && styles.headerRowSubPage]}>
            <View style={styles.titleRow}>
              {parentCrumb && (
                <>
                  <HyperLink onPress={() => navigate(parentCrumb.to)} style={styles.crumbLink}>
                    {parentCrumb.label}
                  </HyperLink>
                  {/* translateY nudges the icon to the text's optical center; flex centers its box ~3px too high. */}
                  <Icon
                    type="rightChevron"
                    width={7}
                    height={12}
                    fill={theme.colors.navy}
                    style={styles.crumbChevron}
                  />
                </>
              )}
              <Text style={styles.headerTitle}>{title}</Text>
            </View>
            {/* Sub-pages show their class/teacher on the right; top-level pages show the tab bar. */}
            {parentCrumb ? (
              headerMeta != null ? (
                <Text style={styles.headerMeta}>{headerMeta}</Text>
              ) : null
            ) : (
              <TabBar activeTab={activeTab} tabs={TABS} onTabChange={(tab) => navigate(`/${tab}`)} />
            )}
          </View>
        </View>

        {scrollable ? (
          <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
            {children}
          </ScrollView>
        ) : (
          <View style={[styles.scroll, styles.content, styles.noScroll]}>{children}</View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: '100vh',
    width: '100%',
    backgroundColor: theme.colors.white,
  },
  sidebar: {
    zIndex: 1,
  },
  main: {
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    gap: theme.spacing.xlarge,
  },
  header: {
    height: HEADER_HEIGHT,
    paddingHorizontal: PADDING,
    backgroundColor: theme.colors.white,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    zIndex: 1,
  },
  headerRow: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.medium,
  },
  // Sub-pages place a smaller meta string opposite the title; align on the text baseline (not box
  // center) so the two different font sizes share a baseline.
  headerRowSubPage: {
    alignItems: 'baseline',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.small,
    flexShrink: 1,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: theme.fontSizes.large,
    fontFamily: theme.fonts.bold,
    fontWeight: theme.fontWeights.xbold,
    color: theme.colors.navy,
  },
  // Normal body-text navy (not muted like the metadata) and regular weight — the student name is
  // bolded to stand out, matching the teacher student-data header. Overrides HyperLink's purple.
  crumbLink: {
    fontSize: theme.fontSizes.large,
    color: theme.colors.navy,
  },
  crumbChevron: {
    transform: [{ translateY: 3 }],
  },
  // Class + teacher on the far right (sub-pages), in place of the tab bar. Navy like the rest of the
  // header; the smaller size keeps it secondary. The row aligns on the text baseline (see
  // headerRowSubPage) so this smaller text still sits on the title's baseline. flexShrink:0 so the
  // title yields first if space is tight.
  headerMeta: {
    fontSize: theme.fontSizes.medium,
    fontFamily: theme.fonts.regular,
    color: theme.colors.navy,
    flexShrink: 0,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingHorizontal: PADDING,
    paddingBottom: theme.spacing.xlarge,
    gap: theme.spacing.medium,
    flexGrow: 1,
  },
  noScroll: {
    overflow: 'hidden',
  },
});
